import { useCallback, useEffect, useMemo, useState } from 'react';
import { Article, ArticleTypeType, ArticleWhereInput, OrderDirection, QueryMode } from 't2-graphql-types';
import { useDebouncedCallback } from 'use-debounce';
import { useAuthenticatedUser } from '../Auth/hooks/useAuthenticatedUser';
import { useLazyPostsQuery } from './hooks/useLazyPostsQuery';
import { useUpdateArticleMutation } from '../ArticleEditor/hooks/useUpdateArticleMutation';
import { ArticleType } from '../Article/types';
import { PromptLinkPostModalContent } from './PromptLinkPostModalContent';
import { getPostsQuery } from './queries/getPostsQuery';
import { getPromptArticlesQuery } from '../PromptArticlesSectionContainer/queries/getPromptArticlesQuery';
import { TGetModalComponent } from '../Modal/useModal';

type TPromptLinkPostModalContentContainerProps = {
  territoryId: string;
  promptId: string;
  modalComponent: TGetModalComponent;
  isOpen: boolean;
};

type TPost = Pick<Article, 'id' | 'title' | 'shortDescription' | 'coverImage'> & {
  isChecked: boolean;
  isLoading: boolean;
};

const take = 6;
const skip = 0;

export const PromptLinkPostModalContentContainer = ({
  promptId,
  territoryId,
  modalComponent: ModalComponent,
  isOpen,
}: TPromptLinkPostModalContentContainerProps) => {
  const [posts, setPosts] = useState<TPost[]>([]);
  const [search, setSearch] = useState<string>('');
  const { authenticatedUserId } = useAuthenticatedUser();
  const [getPosts, { data, loading, called }] = useLazyPostsQuery();
  const [updateArticle] = useUpdateArticleMutation({ refetchQueries: [getPostsQuery, getPromptArticlesQuery] });

  const onSelectArticle = async (postId: string) => {
    try {
      setPosts((prevPosts) =>
        prevPosts.map((post) => {
          const isLoading = post.id === postId;

          return { ...post, isLoading };
        }),
      );

      await updateArticle({
        variables: { where: { id: postId }, data: { parentArticle: { connect: { id: promptId } } } },
      });
    } finally {
      setPosts((prevPosts) => prevPosts.map((post) => ({ ...post, isLoading: false })));
    }
  };

  const baseArticleWhereInput: Partial<ArticleWhereInput> | false = useMemo(
    () =>
      !!authenticatedUserId &&
      !!territoryId && {
        author: {
          id: {
            equals: authenticatedUserId,
          },
        },
        territory: {
          id: {
            equals: territoryId,
          },
        },
        type: {
          equals: ArticleType.Article as unknown as ArticleTypeType,
        },
        status: {
          equals: 'published',
        },
      },
    [authenticatedUserId, territoryId],
  );

  const searchPosts = useCallback(
    (search: string) => {
      const searchWhereInput: Partial<ArticleWhereInput> = {
        title: {
          contains: search,
          mode: 'insensitive' as unknown as QueryMode,
        },
      };

      if (!baseArticleWhereInput) {
        return;
      }

      getPosts({
        variables: {
          take,
          skip,
          where: {
            OR: [
              {
                ...baseArticleWhereInput,
                ...searchWhereInput,
                parentArticle: null,
              },
              {
                ...baseArticleWhereInput,
                ...searchWhereInput,
                parentArticle: {
                  id: {
                    equals: promptId,
                  },
                },
              },
            ],
          },
        },
      });
    },
    [baseArticleWhereInput, getPosts, promptId],
  );

  const debouncedSearch = useDebouncedCallback(searchPosts, 500);

  useEffect(() => {
    if (!authenticatedUserId || !territoryId || search || !isOpen) {
      return;
    }

    getPosts({
      variables: {
        where: {
          OR: [
            {
              ...baseArticleWhereInput,
              parentArticle: null,
            },
            {
              ...baseArticleWhereInput,
              parentArticle: {
                id: { equals: promptId },
              },
            },
          ],
        },
        orderBy: [{ publishedAt: 'desc' as unknown as OrderDirection }],
        skip,
        take,
      },
    });
  }, [getPosts, baseArticleWhereInput, search, isOpen]);

  useEffect(() => {
    if (!search) {
      return;
    }

    debouncedSearch(search);
  }, [search, debouncedSearch]);

  useEffect(() => {
    const nextPosts =
      data?.articles.map((article) => ({
        ...article,
        isChecked: article?.parentArticle?.id === promptId,
        isLoading: false,
      })) || [];
    setPosts(nextPosts);
  }, [data?.articles, promptId]);

  return (
    <ModalComponent
      title="Link an existing Post"
      subtitle="Link an existing post within the Territory. You can only link a post to one prompt at a time."
      childrenContainerClassName="sm:overflow-visible border-b border-t2-neutral-grey03 h-full"
      containerClassName="sm:max-h-full"
    >
      <PromptLinkPostModalContent
        onSearchChange={setSearch}
        search={search}
        onSelectArticle={onSelectArticle}
        posts={posts}
        isLoading={loading}
        isCalled={called}
      />
    </ModalComponent>
  );
};
